import React, { useMemo } from "react";
import {
  HiOutlineCurrencyDollar,
  HiOutlineLocationMarker,
  HiOutlineMail,
} from "react-icons/hi";
import { GrDocumentExcel, GrDocumentTime } from "react-icons/gr";
import { CiMoneyCheck1 } from "react-icons/ci";
import { MdOutlineBalance } from "react-icons/md";
import { TfiBarChart } from "react-icons/tfi";
import { FiUserCheck, FiUsers, FiPhone, FiAlertCircle } from "react-icons/fi";
import { LuBuilding2 } from "react-icons/lu";
import { TbListDetails } from "react-icons/tb";
import {
  getColorScore,
  getLetterScore,
  formatDocument,
  formatToReal,
  countDadosCadastrais,
  formatDateToDDMM,
} from "../../../utils/utils";
import TitleStripe from "../../common/TitleStripe";
import NoOcurrence from "../../common/query/NoOcurrence";
import PendenciasFinanceiras from "../../common/query/PendenciasFinanceiras";
import Protestos from "../../common/query/Protestos";
import ChequesSemFundos from "../../common/query/ChequesSemFundos";
import ChequesSustados from "../../common/query/ChequesSustados";
import AcoesCiveis from "../../common/query/AcoesCiveis";
import HistoricoConsultas from "../../common/query/HistoricoConsultas";
import ParticipacaoEmEmpresas from "../../common/query/ParticipacaoEmEmpresas";
import FalenciaRecuperacaoJudicial from "../../common/query/FalenciaRecuperacaoJudicial";
import Sociedade from "../../common/query/Sociedade";
import QuadroSocietario from "../../common/query/QuadroSocietario";
import Administrador from "../../common/query/Administrador";
import Enderecos from "../../common/query/Enderecos";
import Telefones from "../../common/query/Telefones";
import Emails from "../../common/query/Emails";
import ScoreRating from "./ScoreRating";

/*
  ============================================================================
  Visão consolidada POR TIPO (e não por fonte)
  ----------------------------------------------------------------------------
  Em vez de renderizar o relatório inteiro de cada birô em sequência (o que
  repetia score, cadastro etc. várias vezes), esta view agrupa a informação por
  TIPO. Score e Cadastro aparecem uma vez só (comparativo/consolidado). Já para
  Pendências, Protestos, Cheques e Ações, o tipo é o grupo principal e, DENTRO
  de cada tipo, os dados ficam separados POR FONTE (sub-bloco por fonte,
  reaproveitando os mesmos componentes de detalhe das abas individuais).

  É consumida apenas pela aba "Ver tudo" do TabbedBodyReview e apenas quando há
  2+ fontes de birô. As abas individuais (Fonte 1/2/3/4) continuam mostrando o
  relatório completo de cada birô, sem alteração.

  Fonte da verdade dos grupos: SOURCE_SETS/SOURCE_TITLES em CustomCreditQuery.js
  (aqui há uma cópia mínima só das fontes de birô, para manter o arquivo
  autocontido e a reversão simples).
  ============================================================================
*/

/*
  IMPORTANTE: só entram aqui os birôs de SHAPE PADRÃO (DTO com score/
  resumoConsulta/pendenciasFinanceiras/protestos/etc.), que sabemos decompor por
  tipo. Birôs de shape próprio/bespoke (ex.: defineLimite / defineLimitePositivo,
  que trazem limite + indicadores comportamentais em `compromissos`/
  `pagamento_pontual`) NÃO entram — assim eles não são "decompostos em nada" e
  caem no passthrough do pai, renderizando o componente INTEIRO. Regra geral:
  o que não é padrão renderiza inteiro; nada é descartado.
*/
const BUREAU_SOURCES = [
  {
    source: "boavista",
    title: "Fonte 1",
    keys: [
      "defineRisco",
      "defineNegocioPositivo",
      "acertaEssencialPositivo",
      "acertaEssencial",
      "acertaCompleto",
    ],
  },
  {
    source: "serasa",
    title: "Fonte 2",
    keys: [
      "crednet",
      "serasa",
      "serasaPremium",
      /* Relatório básico PF/PJ: DTO padrão e todas as seções dele (cadastro,
         score, participação, sócios, administradores, pendências, protestos,
         cheques) têm grupo por tipo — decompõe inteiro, sem sobra.

         O serasaRelatorioAvancadoPJ fica de fora de propósito: ele traz seções
         exclusivas (avaliação preliminar de crédito, limite de crédito,
         faturamento presumido, falências/recuperações judiciais) e o componente
         não aceita `hideAgrupados`. Entrar aqui perderia essas seções, e entrar
         como fonte "rica" repetiria tudo que já foi consolidado. Segue no
         passthrough, renderizando inteiro — a regra geral do arquivo. */
      "serasaRelatorioBasicoPF",
      "serasaRelatorioBasicoPJ",
    ],
  },
  {
    source: "spc",
    title: "Fonte 3",
    keys: [
      "spcMixTopPF",
      "spcMixTopPJ",
      "spcSerasaPF",
      "spcSerasaPJ",
      "pendSPCSerasaPF",
    ],
  },
  { source: "quod", title: "Fonte 4", keys: ["quod"] },
];

/* Consultas de protesto nacional: entram na seção "Protestos" do consolidado
   como fonte própria, rotulada "CENPROT" (em vez de "Fonte N"), no shape padrão
   { ocorrencias, resumoConsulta.protestos }. */
const PROTESTO_NACIONAL_KEYS = ["protestoNacional"];

/* Cheques sem fundo (CCF) chegam como módulo próprio, no mesmo shape dos
   protestos nacionais ({ ocorrencias }), e entram na seção "Cheques" do
   consolidado como mais uma fonte. */
const CHEQUES_SEM_FUNDO_KEYS = ["chequesSemFundo"];

/* Conjunto de chaves de birô (+ protesto nacional + CCF) — o pai usa para NÃO
   renderizar esses blocos de novo no passthrough (já estão consolidados aqui). */
export const BUREAU_SOURCE_KEYS = new Set([
  ...BUREAU_SOURCES.flatMap((s) => s.keys),
  ...PROTESTO_NACIONAL_KEYS,
  ...CHEQUES_SEM_FUNDO_KEYS,
]);

/* Módulos cadastrais dedicados que PODEM esconder o card consolidado (só se
   realmente renderem um cadastro próprio). Ficam de fora:
   - os "completo" básicos (cadastralCompleto/…PJ): poucos campos, mais pobres
     que o consolidado por fonte;
   - o receitaFederalPJ: traz um card RICO próprio (sócios, telefones, endereço),
     mas ele renderiza no passthrough de qualquer forma — deixá-lo esconder o
     consolidado fazia o cadastro por fonte (ex.: Define/crednet) sumir.
   Todos esses seguem renderizando seus próprios cards no passthrough; apenas não
   suprimem o consolidado. Sobram os "premium", que já se auto-suprimem em 2+
   fontes (ver abaixo) — ou seja, na prática o consolidado sempre aparece. */
const DEDICATED_CADASTRAL_KEYS = ["cadastralPremiumPF", "cadastralPremiumPJ"];

/* Módulos "premium" só renderizam o cadastro quando o relatório tem menos de 2
   fontes com dadosCadastrais (eles se auto-suprimem — vide CadastralPremium*Query
   `totalDadosCadastrais < 2`). Na visão consolidada há sempre 2+ fontes, então
   eles NÃO mostram cadastro — logo não podem esconder o cadastro consolidado.
   Era esse o motivo do cadastro sumir nas PF (cujo módulo dedicado costuma ser o
   cadastralPremiumPF), enquanto nas PJ o receitaFederalPJ/cadastralCompletoPJ,
   que renderizam sem essa trava, seguiam cobrindo. */
const SELF_SUPPRESSING_CADASTRAL_KEYS = new Set([
  "cadastralPremiumPF",
  "cadastralPremiumPJ",
]);

/* Extrai o DTO (consultaCredito) de um "node" que pode vir embrulhado.
   Birôs adicionais chegam como { data: { consultaCredito } } ou
   { consultaCredito } ou já o próprio DTO — espelha o unwrap do pai
   (it?.data ?? it?.consultaCredito ?? it), mas drilando até consultaCredito. */
function getDto(node) {
  if (!node || typeof node !== "object") return null;
  if (node.consultaCredito) return node.consultaCredito;
  if (node.data && typeof node.data === "object") {
    return node.data.consultaCredito || node.data;
  }
  return node;
}

/* O que passar ao renderComponentByKey do pai como `dataItem`. Diferente do DTO:
   os componentes que embrulham (defineRisco, acerta*, etc.) esperam receber o
   node COM `.consultaCredito`; os que não embrulham (quod, spc*) esperam o DTO
   direto. Esta função devolve exatamente o que o pai passaria. */
function getRenderItem(node) {
  if (!node || typeof node !== "object") return node;
  if (node.consultaCredito) return node; // node com .consultaCredito
  if (node.data && typeof node.data === "object") return node.data; // additional
  return node; // DTO cru (quod, spc...)
}

/* Fontes "ricas": componentes com seções bespoke ALÉM dos tipos padrão
   (comportamental, estimativas, faturamento, falências...). Só para essas
   renderizamos o componente inteiro (com hideAgrupados) para mostrar os extras.
   As demais (crednet, spc*, quod...) são 100% cobertas pelos grupos por tipo. */
const RICH_KEYS = new Set([
  "defineRisco",
  "defineNegocioPositivo",
  "acertaEssencialPositivo",
  "acertaCompleto",
  "acertaEssencial",
]);

/* Coleta uma instância por chave de birô presente no bag (top-level já
   mesclado com additional pelo pai). */
export function collectBureauInstances(mergedBag) {
  if (!mergedBag) return [];
  const out = [];
  for (const { source, title, keys } of BUREAU_SOURCES) {
    for (const key of keys) {
      const node = mergedBag[key];
      const dto = getDto(node);
      if (!dto) continue;

      out.push({
        source,
        title,
        key,
        dto,
        renderItem: getRenderItem(node),
        hasExtras: RICH_KEYS.has(key),
      });
    }
  }
  return out;
}

/* Birô de shape PRÓPRIO (defineLimitePositivo): traz limite + indicadores
   comportamentais num shape que NÃO é o DTO padrão. Não entra em BUREAU_SOURCES
   (o parser padrão não o entende), mas é NORMALIZADO (normalizeDefineDto) para um
   DTO padrão e injetado como instância Fonte 1 — então decompõe nos tipos
   (sócios, administradores, pendências, protestos, cheques, histórico), score e
   cadastro como qualquer birô. O componente inteiro dele segue no passthrough. */
const BESPOKE_SOURCES = [{ key: "defineLimitePositivo", title: "Fonte 1" }];

/* Chaves bespoke — o pai usa para NÃO renderizar o card inteiro de novo no
   passthrough (já entram decompostas nos grupos por tipo/score/cadastro). */
export const BESPOKE_SOURCE_KEYS = new Set(BESPOKE_SOURCES.map((s) => s.key));

export function collectBespokeInstances(mergedBag) {
  if (!mergedBag) return [];
  const out = [];
  for (const { key, title } of BESPOKE_SOURCES) {
    const node = mergedBag[key];
    if (
      node &&
      typeof node === "object" &&
      (node.statusRetorno == 1 || node.score || node.identificacao_completo)
    ) {
      out.push({ key, title, node });
    }
  }
  return out;
}

/* Total de fontes que justificam a visão consolidada: birôs padrão + birôs de
   shape próprio (Define*). O pai usa isto no lugar de instances.length. */
export function countConsolidatableSources(mergedBag) {
  return (
    collectBureauInstances(mergedBag).length +
    collectBespokeInstances(mergedBag).length
  );
}

/* Converte o node bespoke do defineLimitePositivo num DTO padrão (mesmos campos
   que getTypeForInstance/buildCadastro consomem), espelhando exatamente como o
   DefineLimitePositivoQuery prepara cada seção. */
function normalizeDefineDto(node) {
  if (!node || typeof node !== "object") return null;
  const one = (x) => (Array.isArray(x) ? x : x ? [x] : []);
  const s = node.score?.score_pj || node.score?.score_pf || node.score || {};

  const protestos = one(node.protestos?.ultimas_ocorrencias?.protesto);
  const pendencias = one(
    node.pendencias_restricoes?.ultimas_ocorrencias?.pendencia_restricao,
  ).map((it) => ({
    data: it?.data_debito || null,
    valor: it?.valor || 0,
    informante: it?.informante || "",
    tipo: it?.tipo || "",
    documento: it?.documento || "",
    situacao: it?.situacao || "",
    uf: it?.uf || "",
    cidade: it?.cidade || "",
  }));
  const cheques = one(
    node.cheques_sem_fundo?.ultimas_ocorrencias?.cheque_sem_fundo,
  );
  const chequesSustados = one(
    node.cheques_sustados?.ultimas_ocorrencias?.cheques_sustado,
  );
  const acoes = one(node.acoes_civeis?.ultimas_ocorrencias?.acao_civel);
  const historico = one(node.consultas?.ultimas_consultas?.consulta).map(
    (h) => ({ data: h?.data, empresa: h?.razao_social }),
  );
  const socios = node.socios?.mensagem ? [] : one(node.socios?.socio);
  const administradores = node.administradores?.mensagem
    ? []
    : one(node.administradores?.administrador);

  return {
    score: {
      score: s.score,
      probabilidade: s.probabilidade,
      mensagem: s.texto,
    },
    dadosCadastrais: normalizeAltCadastral(node),
    resumoConsulta: {
      protestos: {
        quantidadeTotal: node.protestos?.total_protestos,
        valorTotal: node.protestos?.valor_total,
      },
      pendenciasFinanceiras: {
        quantidadeTotal: node.pendencias_restricoes?.total_pendencias,
        valorTotal: node.pendencias_restricoes?.valor,
      },
      chequesSemFundo: {
        quantidadeTotal: node.cheques_sem_fundo?.total_cheques_sem_fundos,
      },
      chequesSustados: {
        quantidadeTotal: node.cheques_sustados?.total,
      },
      acoesCiveis: {
        quantidadeTotal: node.acoes_civeis?.total_acoes,
        valorTotal: node.acoes_civeis?.valor_total,
      },
    },
    protestos,
    pendenciasFinanceiras: pendencias,
    chequesSemFundo: cheques,
    chequesSustados,
    acoesCiveis: acoes,
    historicoConsultas: historico,
    historicoConsultasResumo: {
      totaisHistoricoConsultas: [node.consultas?.quantidade_total],
    },
    consultasMes: one(node.consultas?.consultas_mes),
    socios,
    administradores,
    /* Repassado no shape original: a seção de participações dos sócios entende
       as duas formas, e sem isto a Fonte 1 ficava de fora dela. */
    participacoes_socios_adm_outras_empresas:
      node.participacoes_socios_adm_outras_empresas,
  };
}

/* Instância Fonte 1 do defineLimitePositivo já normalizada (DTO padrão), para
   entrar nos grupos por tipo, no score e no cadastro como um birô comum. */
export function buildDefineInstance(mergedBag) {
  const node = mergedBag?.defineLimitePositivo;
  if (!node || typeof node !== "object") return null;
  if (!(node.statusRetorno == 1 || node.score || node.identificacao_completo)) {
    return null;
  }
  const dto = normalizeDefineDto(node);
  if (!dto) return null;
  return {
    source: "boavista",
    title: "Fonte 1",
    key: "defineLimitePositivo",
    dto,
    renderItem: node,
    // Renderiza via ExtrasPorFonte com hideAgrupados: só as seções exclusivas
    // (faturamento, comportamental, limite, participações, falências…), já que
    // os tipos/score/cadastro entram decompostos.
    hasExtras: true,
    isDefine: true,
  };
}

/* Instâncias CENPROT (protestoNacional): shape próprio
   ({ ocorrencias, resumoConsulta.protestos }) normalizado para um DTO que só
   reporta o tipo "protestos" — assim os protestos nacionais entram na MESMA
   seção dos protestos de birô, num bloco de fonte rotulado "CENPROT". Quando a
   consulta falhou (statusRetorno != 1), a instância entra marcada como
   indisponível, para o aviso de indisponibilidade continuar visível dentro da
   seção (era o comportamento do bloco avulso, que deixou de renderizar). */
/* Consultas cadastrais internas (cadastralCompleto/cadastralCompletoPJ): os
   dados cadastrais delas entram na seção "Cadastro" consolidada como fonte
   própria, rotulada "Fonte interna" (mesmo card compacto das demais fontes).
   O restante do componente (parentescos, filiais, endereços, telefones…)
   segue renderizando no passthrough — os pais usam estas chaves para passar
   hideCadastro ao componente e não repetir o card de dados cadastrais. */
export const CADASTRO_INTERNO_KEYS = new Set([
  "cadastralCompleto",
  "cadastralCompletoPJ",
]);

export function buildCadastroInternoInstances(mergedBag) {
  const out = [];
  for (const key of CADASTRO_INTERNO_KEYS) {
    const node = mergedBag?.[key];
    if (!node || typeof node !== "object") continue;
    const cad = getDto(node)?.dadosCadastrais;
    if (!cad || typeof cad !== "object") continue;
    // Sem nada exibível, não cria o bloco (e o pai então NÃO esconde o card
    // próprio do componente — nada some).
    if (!buildCadastro(cad)) continue;
    out.push({
      source: "interna",
      title: "Fonte interna",
      key,
      dto: { dadosCadastrais: cad },
      renderItem: node,
      hasExtras: false,
      isCadastroInterno: true,
    });
  }
  return out;
}

/* O módulo de cheques sem fundo (CCF) entra como fonte da seção "Cheques" do
   consolidado, em vez de abrir bloco próprio: a seção já existe e já separa por
   fonte, então o CCF fica ao lado dos cheques dos birôs, comparável.

   Lista vazia continua virando instância — é o "nada consta" da fonte dentro da
   seção, que some se a chave for simplesmente descartada. */
export function buildChequesSemFundoInstances(mergedBag) {
  const out = [];
  for (const key of CHEQUES_SEM_FUNDO_KEYS) {
    const node = mergedBag?.[key];
    if (!node || typeof node !== "object") continue;

    const ok = node.statusRetorno == 1;
    const ocorrencias = ok ? arr(node.ocorrencias) : [];

    out.push({
      source: "ccf",
      title: "CCF",
      key,
      dto: {
        chequesSemFundo: ocorrencias,
        resumoConsulta: {
          chequesSemFundo: {
            quantidadeTotal:
              node.resumoConsulta?.chequesSemFundo?.quantidadeTotal ??
              ocorrencias.length,
          },
        },
      },
      renderItem: node,
      hasExtras: false,
      indisponivel: !ok,
    });
  }
  return out;
}

export function buildProtestoNacionalInstances(mergedBag) {
  const out = [];
  for (const key of PROTESTO_NACIONAL_KEYS) {
    const node = mergedBag?.[key];
    if (!node || typeof node !== "object") continue;
    const ok = node.statusRetorno == 1;
    let dto = { protestos: [] };
    if (ok) {
      dto = {
        protestos: arr(node.ocorrencias),
        resumoConsulta: { protestos: node.resumoConsulta?.protestos },
      };
    }
    out.push({
      source: "cenprot",
      title: "CENPROT",
      key,
      dto,
      renderItem: node,
      hasExtras: false,
      isProtestoNacional: true,
      indisponivel: !ok,
    });
  }
  return out;
}

/* Ordena as instâncias por fonte: Fonte 1 antes da 2, 3… (rótulos sem número,
   como "CENPROT", vão para o fim). */
function fonteRank(title) {
  const m = /Fonte\s*(\d+)/i.exec(title || "");
  return m ? Number(m[1]) : 999;
}
function sortByFonte(list) {
  return [...list].sort((a, b) => fonteRank(a.title) - fonteRank(b.title));
}

/* ===== Helpers ===== */
const arr = (x) => (Array.isArray(x) ? x : []);
const toInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
function parseValor(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (v == null) return 0;
  let s = String(v).replace(/[^\d,.-]/g, "");
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}
const sumValor = (items) =>
  items.reduce((acc, it) => acc + parseValor(it?.valor), 0);

/* Retorna os dados de um TIPO para UMA fonte: itens + totais + se a fonte
   sequer reporta esse tipo (present). Totais preferem o resumoConsulta da
   própria fonte e caem para o cálculo pelos itens quando ausentes. */
function getTypeForInstance(inst, type) {
  const { dto } = inst;
  const rc = dto.resumoConsulta || {};

  const withTotals = (items, resumo, present) => ({
    items,
    present,
    quantidadeTotal: toInt(resumo?.quantidadeTotal) || items.length,
    valorTotal: parseValor(resumo?.valorTotal) || sumValor(items),
  });

  if (type === "pendencias") {
    // Fonte 3 (SPC): cada produto guarda as pendências em campos diferentes do
    // DTO — e alguns em MAIS de um ao mesmo tempo: spcMixTop em spc (variante
    // 2: pendenciasSpc); spcSerasa em pendenciasSerasa + pendenciasSpc
    // (variante Score PJ: pendenciasFinanceiras + spc); pendSPCSerasaPF em
    // pendenciasFinanceiras. Junta TODOS os campos presentes, descartando
    // aliases com o mesmo conteúdo (após serializar, só a igualdade de
    // conteúdo identifica o alias), e soma os resumos também deduplicados.
    if (inst.source === "spc") {
      const dedupByContent = (candidates) => {
        const seen = new Set();
        const out = [];
        for (const v of candidates) {
          if (v == null) continue;
          const sig = JSON.stringify(v);
          if (seen.has(sig)) continue;
          seen.add(sig);
          out.push(v);
        }
        return out;
      };
      const listas = dedupByContent(
        [
          dto.pendenciasFinanceiras,
          dto.pendenciasSerasa,
          dto.serasa,
          dto.pendenciasSpc,
          dto.spc,
        ].filter(Array.isArray),
      );
      const resumos = dedupByContent(
        [rc.pendenciasFinanceiras, rc.serasa, rc.spc].filter(
          (r) => r && typeof r === "object",
        ),
      );
      const items = listas.flat();
      const qtd = resumos.reduce(
        (acc, r) => acc + toInt(r?.quantidadeTotal),
        0,
      );
      const val = resumos.reduce(
        (acc, r) => acc + parseValor(r?.valorTotal),
        0,
      );
      return {
        items,
        present: listas.length > 0,
        quantidadeTotal: qtd || items.length,
        valorTotal: val || sumValor(items),
      };
    }
    return withTotals(
      arr(dto.pendenciasFinanceiras),
      rc.pendenciasFinanceiras,
      Array.isArray(dto.pendenciasFinanceiras),
    );
  }

  /* Renda/faturamento de cada fonte. A PH3A entrega números em `dto.renda`; os
     birôs entregam texto já formatado dentro do cadastro (rendaPresumida,
     faixa salarial, classe social). Os dois viram o mesmo item para a seção
     comparar fonte a fonte. */
  if (type === "renda") {
    const cad = dto.dadosCadastrais || {};
    const doCadastro = {
      presumida:
        cad.rendaPresumida || cad.rendaEstimada || cad.textoRendaPresumida,
      faturamento: cad.faturamentoPresumido,
      faixa: cad.rendaFaixaSalarial || cad.faixaFaturamento,
      classe: cad.classeSocial,
      faturamentoMedio: cad.faturamentoMedioCNAE,
    };

    const item = dto.renda
      ? { ...doCadastro, ...dto.renda }
      : Object.values(doCadastro).some((v) => !isBlank(v))
        ? doCadastro
        : null;

    return {
      items: item ? [item] : [],
      present: !!item,
      quantidadeTotal: 0,
      valorTotal: 0,
    };
  }

  if (type === "protestos") {
    return withTotals(
      arr(dto.protestos),
      rc.protestos,
      Array.isArray(dto.protestos),
    );
  }
  if (type === "cheques") {
    return withTotals(
      arr(dto.chequesSemFundo),
      rc.chequesSemFundo,
      Array.isArray(dto.chequesSemFundo),
    );
  }
  if (type === "chequesSustados") {
    return withTotals(
      arr(dto.chequesSustados),
      rc.chequesSustados,
      Array.isArray(dto.chequesSustados),
    );
  }
  if (type === "acoes") {
    return withTotals(
      arr(dto.acoesCiveis),
      rc.acoesCiveis,
      Array.isArray(dto.acoesCiveis),
    );
  }
  if (type === "falencias") {
    const items = arr(dto.falenciasRecJudiciais);
    return {
      items,
      present: items.length > 0,
      quantidadeTotal: items.length,
      valorTotal: 0,
    };
  }
  if (type === "historico") {
    const items = arr(dto.historicoConsultas);
    const qtd = items.length;
    const chartSeries = buildConsultasMes(dto);
    return {
      items,
      present: Array.isArray(dto.historicoConsultas) || chartSeries.length > 0,
      quantidadeTotal: qtd,
      valorTotal: 0,
      chartSeries,
    };
  }

  // PF x PJ: numa PF (cpf), o campo `socios` do birô traz na verdade as
  // PARTICIPAÇÕES da pessoa em empresas — não o quadro societário. Espelha o
  // CrednetQuery: PF → Participação em empresas (participacaoEmEmpresas || socios);
  // Quadro societário só aparece em PJ (cnpj).
  const isPF = !!dto?.dadosCadastrais?.cpf && !dto?.dadosCadastrais?.cnpj;
  if (type === "participacao") {
    const proprias = arr(dto.participacaoEmEmpresas);
    const items = proprias.length ? proprias : isPF ? arr(dto.socios) : [];

    /* `present` marca que a fonte REPORTA o tipo, não que ela achou algo —
       mesma regra de pendências/protestos/cheques logo acima. Antes era
       `items.length > 0`, e o birô que consultou e não achou participação
       nenhuma (o acertaCompleto devolve `participacaoEmEmpresas: []`) sumia da
       lista de blocos; quando NENHUMA fonte tinha participação, a seção
       inteira deixava de existir. Não dá para distinguir "não foi consultado"
       de "consultou e nada consta" olhando uma seção que não está lá. */
    const reporta =
      Array.isArray(dto.participacaoEmEmpresas) ||
      (isPF && Array.isArray(dto.socios));

    return {
      items,
      present: items.length > 0 || reporta,
      quantidadeTotal: items.length,
      valorTotal: 0,
    };
  }
  if (type === "socios") {
    const items = isPF ? [] : arr(dto.socios);
    return {
      items,
      present: items.length > 0,
      quantidadeTotal: items.length,
      valorTotal: 0,
    };
  }

  // Blocos cadastrais/relacionais: só aparecem quando há dados (não mostram
  // "Nada consta" por fonte, para não poluir).
  const cadastralField = {
    administradores: "administradores",
    enderecos: "enderecos",
    emails: "emails",
  }[type];
  if (cadastralField) {
    const items = arr(dto[cadastralField]);
    return {
      items,
      present: items.length > 0,
      quantidadeTotal: items.length,
      valorTotal: 0,
    };
  }
  if (type === "telefones") {
    const items = arr(dto.telefonesCadastrais ?? dto.telefones);
    return {
      items,
      present: items.length > 0,
      quantidadeTotal: items.length,
      valorTotal: 0,
    };
  }

  return { items: [], present: false, quantidadeTotal: 0, valorTotal: 0 };
}

/* Série de "consultas por mês" para o gráfico do histórico. O Define já traz
   consultasMes ([{ mes_ano, quantidade }]); os birôs padrão trazem pares
   mês/ano + totais paralelos no resumo. */
function buildConsultasMes(dto) {
  if (Array.isArray(dto?.consultasMes) && dto.consultasMes.length) {
    return dto.consultasMes
      .map((it) => ({
        mes_ano: it?.mes_ano,
        quantidade: Number(it?.quantidade) || 0,
      }))
      .filter((it) => it.mes_ano);
  }
  const resumo = dto?.historicoConsultasResumo || {};
  const pares = arr(resumo.paresMesAnoHistoricoConsultas);
  const totais = arr(resumo.totaisHistoricoConsultas);
  return pares
    .map((mes_ano, i) => ({ mes_ano, quantidade: Number(totais[i]) || 0 }))
    .filter((it) => it.mes_ano);
}

/* Gráfico de barras "Quantidade x Consultas/mês" — mesmo visual do Define,
   reaproveitado no histórico consolidado. */
function ConsultasPorMesChart({ series = [] }) {
  if (!series.length) return null;
  const maxValue = Math.max(
    1,
    ...series.map((it) => Number(it?.quantidade) || 0),
  );
  return (
    <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">
        Quantidade x Consultas/mês (últimos meses)
      </div>
      <div className="p-4">
        <div className="overflow-x-auto">
          <div className="flex items-end justify-center gap-4 min-w-max">
            {series.map((item, index) => {
              const value = Number(item?.quantidade) || 0;
              const pct = Math.round((value / maxValue) * 100);
              return (
                <div
                  key={index}
                  className="flex w-14 flex-col items-center gap-2"
                >
                  <div className="flex h-28 w-full items-end rounded-md bg-slate-50">
                    <div
                      className="w-full rounded-t-md bg-slate-800/80"
                      style={{ height: `${pct}%` }}
                    />
                  </div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                    {formatDateToDDMM(item?.mes_ano)}
                  </div>
                  <div className="text-xs font-semibold text-slate-700 tabular-nums">
                    {value}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Detalhe de um tipo para uma fonte — reaproveita os mesmos componentes das
   abas individuais, para o visual ficar idêntico. */
function renderDetail(type, block, inst) {
  const { items, quantidadeTotal, valorTotal } = block;
  if (type === "pendencias") {
    return (
      <PendenciasFinanceiras
        pendenciasFinanceiras={items}
        quantidadeTotal={quantidadeTotal}
        valorTotal={valorTotal}
        temDataDisponibilidade={false}
      />
    );
  }
  if (type === "protestos") {
    return (
      <Protestos
        protestos={items}
        quantidadeTotal={quantidadeTotal}
        valorTotal={valorTotal}
        temVencimento={false}
        isNacional={!!inst?.isProtestoNacional}
      />
    );
  }
  if (type === "renda") {
    return <RendaDetalhe renda={items[0]} />;
  }
  if (type === "cheques") {
    return (
      <ChequesSemFundos
        chequesSemFundos={items}
        quantidadeTotal={quantidadeTotal}
      />
    );
  }
  if (type === "chequesSustados") {
    return (
      <ChequesSustados
        chequesSustados={items}
        quantidadeTotal={quantidadeTotal}
      />
    );
  }
  if (type === "acoes") {
    return (
      <AcoesCiveis
        acoesCiveis={items}
        quantidadeTotal={quantidadeTotal}
        valorTotal={valorTotal}
      />
    );
  }
  if (type === "falencias") {
    // O DTO chama o tipo da ocorrência de `tipo`; o componente espera
    // `tipo_ocorrencia` (nome que veio do XML do birô).
    return (
      <FalenciaRecuperacaoJudicial
        falenciaRecuperacaoJudicial={items.map((ocorrencia) => ({
          ...ocorrencia,
          tipo_ocorrencia: ocorrencia?.tipo_ocorrencia ?? ocorrencia?.tipo,
        }))}
      />
    );
  }
  if (type === "historico") {
    return (
      <>
        <HistoricoConsultas
          historicoConsultas={items}
          quantidadeTotal={quantidadeTotal}
        />
        <ConsultasPorMesChart series={block.chartSeries} />
      </>
    );
  }
  if (type === "participacao") {
    return <ParticipacaoEmEmpresas empresas={items} hasStatus hasNegative />;
  }
  if (type === "socios") {
    // O Define traz sócios/admin em shape próprio — usa os componentes dele.
    return inst?.isDefine ? (
      <QuadroSocietario socios={items} />
    ) : (
      <Sociedade socios={items} />
    );
  }
  if (type === "administradores") {
    return inst?.isDefine ? (
      <Administrador administradores={items} />
    ) : (
      <Sociedade socios={items} />
    );
  }
  if (type === "enderecos") {
    return <Enderecos enderecos={items} />;
  }
  if (type === "telefones") {
    return <Telefones telefones={items} />;
  }
  if (type === "emails") {
    return <Emails emails={items} />;
  }
  return null;
}

/* Normaliza a probabilidade de inadimplência para a faixa 0–100%.
   As fontes variam: às vezes fração (0,25 = 25%), às vezes já em % (25), e às
   vezes numa escala inflada (ex.: 2500, que deveria ser 25). Regras:
   - fração (0–1)  -> multiplica por 100 (0,25 -> 25)
   - já em % (1–100) -> mantém
   - grande (>100) -> divide por 10 até cair na faixa 0–100 (2500 -> 25) */
function normalizeProbabilidade(raw) {
  if (raw == null || raw === "") return null;
  let n = Number(raw);
  if (!Number.isFinite(n)) return null;
  n = Math.abs(n);
  if (n === 0) return 0;
  if (n <= 1) n = n * 100;
  while (n > 100) n = n / 10;
  return n;
}

/* ===== Score comparativo ===== */
function extractScores(instances) {
  const out = [];
  for (const { dto, title } of instances) {
    // Fonte sem a chave `score` no DTO não entra no comparativo (ex.: produto
    // que nem reporta score). O card "não foi possível calcular" fica só para
    // fontes que RESPONDERAM com a chave, mas sem nota válida (0/2/vazio).
    if (dto?.score == null) continue;

    const rawValue = dto?.score?.score;
    const raw = rawValue == null || rawValue === "" ? null : Number(rawValue);
    const unavailable = raw == null || raw === 0 || raw === 2;

    if (raw !== null && !Number.isFinite(raw)) continue;

    const pi = dto?.score?.probabilidadeInadimplencia;
    const p = dto?.score?.probabilidade;
    const rawProb = pi != null && pi !== "" ? pi : p;
    const prob = normalizeProbabilidade(rawProb);

    out.push({
      title,
      score: raw,
      prob,
      unavailable,
      mensagem: dto?.score?.mensagem,
    });
  }
  return out;
}

/* ===== UI ===== */
/* Isola cada bloco: se um componente de detalhe estourar com algum formato de
   dado inesperado, mostra um aviso no lugar (e loga qual foi) em vez de derrubar
   a view inteira. */
class SafeRender extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }
  static getDerivedStateFromError(err) {
    return { err };
  }
  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error(
      "[ConsolidatedByType] bloco falhou:",
      this.props.label,
      err,
      info,
    );
  }
  render() {
    if (this.state.err) {
      return (
        <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
          Não foi possível exibir este bloco
          {this.props.label ? ` (${this.props.label})` : ""}.
        </div>
      );
    }
    return this.props.children;
  }
}

function FonteBlock({ title, children }) {
  return (
    <div className="mt-6">
      <div className="mb-3 flex items-center gap-2.5">
        <span className="h-4 w-1 rounded-full bg-blue-500" />
        <span className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
          {title}
        </span>
      </div>
      {children}
    </div>
  );
}

/* Aviso exibido no bloco de fonte quando a consulta não respondeu (hoje só o
   protesto nacional/CENPROT entra nesse estado — antes ele mostrava esse aviso
   no bloco avulso, que deixou de renderizar na visão consolidada). */
function FonteIndisponivel() {
  return (
    <div className="bg-gray-100 p-4 rounded-lg text-xs flex gap-2 items-center">
      <FiAlertCircle />
      <div>
        Consulta{" "}
        <span className="font-semibold">temporariamente indisponível</span> no
        momento. Você pode consultar as informações diretamente no site oficial:{" "}
        <a
          className="underline"
          target="_blank"
          rel="noreferrer"
          href="https://www.pesquisaprotesto.com.br"
        >
          Clicando aqui
        </a>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Seções "quem é quem" (quadro societário e administradores) saem numa tabela
   só, com todas as fontes juntas. Um bloco por fonte repetia o mesmo sócio três
   vezes sem acrescentar nada: o que interessa é a lista consolidada de pessoas.
   A coluna Fonte guarda a procedência de cada linha.

   Shape único: o Define traz nome_empresa/cpf_cnpj/data_entrada e os birôs
   padrão nome/cpf/cargo — aqui viram um registro só, para as duas origens
   caberem na mesma tabela sem perder coluna.
   ──────────────────────────────────────────────────────────────────────────── */
const MERGED_TYPES = new Set(["socios", "administradores"]);

const soDigitos = (valor) => String(valor ?? "").replace(/\D/g, "");
const nomeNormalizado = (valor) =>
  String(valor ?? "")
    .trim()
    .toUpperCase();
const vazio = (valor) =>
  valor == null || valor === "" || valor === "-" || valor === undefined;

function normalizarPessoa(item) {
  return {
    nome_empresa: item?.nome_empresa || item?.nome || "",
    nome: item?.nome || item?.nome_empresa || "",
    cpf_cnpj: item?.cpf_cnpj || item?.cpf || "",
    cpf: item?.cpf || item?.cpf_cnpj || "",
    data_entrada: item?.data_entrada || item?.entrada || "",
    entrada: item?.entrada || item?.data_entrada || "",
    cargo: item?.cargo || "",
    participacao:
      item?.participacao ??
      item?.participacaoEmPorcentagem ??
      item?.percentualParticipacao ??
      "",
    assina_empresa: item?.assina_empresa || "",
    indicador_debito: item?.indicador_debito || "",
    restricoes_documento: item?.restricoes_documento ?? null,
    temRestricoes: item?.temRestricoes ?? null,
  };
}

/* Restrição não se resolve por "quem chegou primeiro": se QUALQUER fonte aponta
   restrição na pessoa, a linha tem que mostrar restrição. Com o preenchimento
   normal (primeiro valor não-vazio vence) um `temRestricoes: false` da Fonte 1
   bloqueava o `true` da Fonte 2 — `false` não é vazio —, e o sócio aparecia
   limpo no consolidado enquanto a aba da outra fonte o mostrava restrito. */
const CAMPOS_DE_RESTRICAO = new Set([
  "temRestricoes",
  "indicador_debito",
  "restricoes_documento",
]);

const indicaRestricao = (valor) => {
  if (valor == null) return false;
  if (typeof valor === "boolean") return valor;
  if (Array.isArray(valor)) return valor.length > 0;
  return /^(sim|s|true|1)$/i.test(String(valor).trim());
};

// Vence o valor que ACUSA restrição; entre dois que acusam, o que tem detalhe.
function juntarRestricao(atual, novo) {
  if (indicaRestricao(novo) && !indicaRestricao(atual)) return novo;
  if (!indicaRestricao(novo)) return vazio(atual) ? novo : atual;
  if (Array.isArray(novo) && Array.isArray(atual)) {
    return novo.length > atual.length ? novo : atual;
  }
  return vazio(atual) ? novo : atual;
}

/* Mesma pessoa em duas fontes vira uma linha: casa por documento e, sem
   documento, por nome. O campo que uma fonte não trouxe é preenchido pela
   outra; quando as duas trazem valores diferentes (ex.: participação), vale a
   primeira fonte na ordem (Fonte 1 antes da 2) e a coluna Fonte mostra que há
   mais de uma origem — a divergência continua visível na aba individual.
   A exceção são os campos de restrição, que somam em vez de escolher. */
function juntarPessoasDasFontes(blocks) {
  const porChave = new Map();

  blocks.forEach((bloco, idxBloco) => {
    bloco.items.forEach((item, idxItem) => {
      const pessoa = normalizarPessoa(item);
      const chave =
        soDigitos(pessoa.cpf_cnpj) ||
        nomeNormalizado(pessoa.nome_empresa) ||
        `sem-identificacao-${idxBloco}-${idxItem}`;

      const atual = porChave.get(chave);
      if (!atual) {
        porChave.set(chave, { ...pessoa, fontes: [bloco.inst.title] });
        return;
      }

      Object.entries(pessoa).forEach(([campo, valor]) => {
        if (CAMPOS_DE_RESTRICAO.has(campo)) {
          atual[campo] = juntarRestricao(atual[campo], valor);
          return;
        }
        if (vazio(atual[campo]) && !vazio(valor)) atual[campo] = valor;
      });
      if (!atual.fontes.includes(bloco.inst.title)) {
        atual.fontes.push(bloco.inst.title);
      }
    });
  });

  return [...porChave.values()].map(({ fontes, ...pessoa }) => ({
    ...pessoa,
    fonte: fontes.join(" · "),
  }));
}

/* ────────────────────────────────────────────────────────────────────────────
   Participações DOS SÓCIOS/ADMINISTRADORES em outras empresas.

   Diferente de "Participação em empresas", que é da empresa consultada. Aqui o
   dono da participação é a pessoa, e a fonte manda uma entrada por participação
   — o mesmo sócio repetido oito vezes. Jogar tudo numa lista só respondia
   "quais empresas aparecem", nunca "de quem é cada uma"; por isso agrupa por
   pessoa e cada uma ganha sua própria tabela.
   ──────────────────────────────────────────────────────────────────────────── */
function coletarParticipacoesDeSocios(dto) {
  const saida = [];

  // Shape novo (formatter atual): [{ socio: {...}, participacoes: [...] }]
  arr(dto?.participacoesSociosAdmOutrasEmpresas).forEach((node) => {
    saida.push({
      nome: node?.socio?.nome,
      cpf: node?.socio?.cpf,
      cargo: node?.socio?.cargo,
      participacoes: arr(node?.participacoes).map((p) => ({
        empresa: p?.empresa || p?.razao_social,
        cnpj: p?.cnpj,
        funcao: p?.funcao,
        status: p?.status || p?.situacao_cnpj,
        dataEntrada: p?.dataEntrada || p?.entrada,
        participacaoEmPorcentagem:
          p?.participacaoEmPorcentagem ?? p?.participacao,
        temRestricao: p?.temRestricao,
      })),
    });
  });

  // Shape antigo (XML do birô): { socio: [{ socios_participantes: [...] }] }
  const antigo = dto?.participacoes_socios_adm_outras_empresas;
  (Array.isArray(antigo?.socio)
    ? antigo.socio
    : antigo?.socio
      ? [antigo.socio]
      : []
  ).forEach((socio) => {
    saida.push({
      nome: socio?.nome_socio || socio?.nome,
      cpf: socio?.cpf_socio || socio?.cpf,
      cargo: socio?.cargo_socio || socio?.cargo,
      participacoes: arr(socio?.socios_participantes).map((p) => ({
        empresa: p?.razao_social || p?.empresa,
        cnpj: p?.cnpj,
        funcao: p?.funcao,
        status: p?.situacao_cnpj || p?.status,
        dataEntrada: p?.entrada || p?.data_entrada,
        participacaoEmPorcentagem: p?.participacao,
        /* Este shape não traz o booleano: o que existe é o indicador de débito
           da empresa participada ("Sim"/"Não"), que é a mesma pergunta que a
           coluna faz. Sem ele a coluna sumiria justo nesta fonte. */
        temRestricao:
          p?.temRestricao ??
          (p?.indicador_debito != null
            ? indicaRestricao(p.indicador_debito)
            : undefined),
      })),
    });
  });

  return saida;
}

/* Agrupa por PESSOA, não por nome+documento: a mesma fonte às vezes grafa o
   nome de dois jeitos ("BERWERTH"/"BERWETH") com o mesmo CPF, e a chave composta
   partia o sócio em dois blocos. Com CPF presente ele manda; o nome só entra
   como chave quando não há documento. */
function juntarParticipacoesPorSocio(blocks) {
  const porPessoa = new Map();

  blocks.forEach((bloco, idxBloco) => {
    coletarParticipacoesDeSocios(bloco.inst.dto).forEach((socio, idxSocio) => {
      const chave =
        soDigitos(socio.cpf) ||
        nomeNormalizado(socio.nome) ||
        `sem-identificacao-${idxBloco}-${idxSocio}`;

      if (!porPessoa.has(chave)) {
        porPessoa.set(chave, {
          nome: socio.nome,
          cpf: socio.cpf,
          cargo: socio.cargo,
          fontes: [],
          empresas: new Map(),
        });
      }

      const atual = porPessoa.get(chave);
      if (vazio(atual.nome) && !vazio(socio.nome)) atual.nome = socio.nome;
      if (vazio(atual.cargo) && !vazio(socio.cargo)) atual.cargo = socio.cargo;
      if (!atual.fontes.includes(bloco.inst.title)) {
        atual.fontes.push(bloco.inst.title);
      }

      socio.participacoes.forEach((empresa, idxEmpresa) => {
        const chaveEmpresa =
          soDigitos(empresa.cnpj) ||
          nomeNormalizado(empresa.empresa) ||
          `sem-cnpj-${idxBloco}-${idxSocio}-${idxEmpresa}`;

        const existente = atual.empresas.get(chaveEmpresa);
        if (!existente) {
          atual.empresas.set(chaveEmpresa, { ...empresa });
          return;
        }
        // Mesma empresa vinda de duas fontes: completa buracos e mantém a
        // restrição de quem a reportou.
        Object.entries(empresa).forEach(([campo, valor]) => {
          if (campo === "temRestricao") {
            existente.temRestricao = juntarRestricao(
              existente.temRestricao,
              valor,
            );
            return;
          }
          if (vazio(existente[campo]) && !vazio(valor))
            existente[campo] = valor;
        });
      });
    });
  });

  return [...porPessoa.values()]
    .map((pessoa) => ({
      ...pessoa,
      empresas: [...pessoa.empresas.values()],
      fonte: pessoa.fontes.join(" · "),
    }))
    .filter((pessoa) => pessoa.empresas.length);
}

function ParticipacoesSociosSection({ icon, titulo, instances }) {
  const socios = useMemo(
    () => juntarParticipacoesPorSocio(instances.map((inst) => ({ inst }))),
    [instances],
  );

  if (!socios.length) return null;

  return (
    <div className="mt-2">
      <TitleStripe className="mb-2">
        <div className="flex items-center gap-2">
          {icon}
          <span>{titulo}</span>
        </div>
      </TitleStripe>
      <SafeRender label={titulo}>
        <div className="space-y-4">
          {socios.map((socio, index) => (
            <div
              key={`${socio.cpf || socio.nome || index}`}
              className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2.5">
                <div className="min-w-0">
                  <p className="font-semibold leading-tight text-slate-900">
                    {socio.nome || "Sócio/Adm"}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {socio.cpf ? `CPF/CNPJ: ${formatDocument(socio.cpf)}` : "—"}
                    {socio.cargo ? ` · ${socio.cargo}` : ""}
                    {` · ${socio.empresas.length} ${
                      socio.empresas.length === 1 ? "empresa" : "empresas"
                    }`}
                  </p>
                </div>
                {socio.fonte && (
                  <span className="shrink-0 rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-blue-700">
                    {socio.fonte}
                  </span>
                )}
              </div>
              <div className="px-4 pb-4">
                <ParticipacaoEmEmpresas
                  empresas={socio.empresas}
                  hasStatus
                  hasNegative
                />
              </div>
            </div>
          ))}
        </div>
      </SafeRender>
    </div>
  );
}

function MergedPessoasSection({ icon, titulo, type, blocks }) {
  const pessoas = useMemo(() => juntarPessoasDasFontes(blocks), [blocks]);

  if (!pessoas.length) return null;

  return (
    <div className="mt-2">
      <TitleStripe className="mb-2">
        <div className="flex items-center gap-2">
          {icon}
          <span>{titulo}</span>
        </div>
      </TitleStripe>
      <SafeRender label={titulo}>
        {type === "administradores" ? (
          <Administrador administradores={pessoas} />
        ) : (
          <QuadroSocietario socios={pessoas} />
        )}
      </SafeRender>
    </div>
  );
}

/* Renda de uma fonte. Valor principal em destaque e, quando a fonte manda os
   números (e não texto pronto), as barras comparando renda do titular, da
   família e do cônjuge — proporcionais ao maior, porque as rendas se sobrepõem
   e somar não significaria nada. */
function RendaDetalhe({ renda }) {
  if (!renda) return null;

  const dinheiro = (valor) =>
    Number(valor) > 0 ? formatToReal(valor) : isBlank(valor) ? null : valor;

  const principal = dinheiro(renda.presumida) || dinheiro(renda.faturamento);
  const rotulo = renda.presumida ? "Renda presumida" : "Faturamento presumido";

  const linhas = [
    { label: "Presumida", valor: Number(renda.presumida) },
    { label: "Familiar", valor: Number(renda.familiar) },
    { label: "Cônjuge", valor: Number(renda.conjuge) },
    { label: "Faturamento", valor: Number(renda.faturamento) },
    { label: "Balanço", valor: Number(renda.balanco) },
    { label: "Capital social", valor: Number(renda.capitalSocial) },
  ].filter((linha) => Number.isFinite(linha.valor) && linha.valor > 0);

  const maior = Math.max(...linhas.map((linha) => linha.valor), 0);
  const atualizacao = /^\d{8}$/.test(String(renda.atualizacao ?? ""))
    ? `${String(renda.atualizacao).slice(4, 6)}/${String(
        renda.atualizacao,
      ).slice(0, 4)}`
    : null;

  const chips = [
    renda.faixa && `Faixa: ${renda.faixa}`,
    renda.classe && `Classe: ${renda.classe}`,
    renda.faturamentoMedio && `Médio do CNAE: ${renda.faturamentoMedio}`,
  ].filter(Boolean);

  if (!principal && !linhas.length && !chips.length) return null;

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-3 border-b border-slate-100 bg-gradient-to-br from-slate-50 to-white px-5 py-4">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
            {rotulo}
          </div>
          <div className="mt-0.5 text-3xl font-bold leading-none text-violet-600">
            {principal || "—"}
          </div>
          <div className="mt-1 text-[11px] text-slate-400">
            estimada pelo modelo da fonte
          </div>
        </div>

        {chips.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {chips.map((chip) => (
              <span
                key={chip}
                className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600"
              >
                {chip}
              </span>
            ))}
          </div>
        )}
      </div>

      {linhas.length > 1 && (
        <div className="space-y-2.5 px-5 py-4">
          {linhas.map((linha) => (
            <div key={linha.label} className="flex items-center gap-3">
              <span className="w-24 shrink-0 text-xs font-semibold text-slate-500">
                {linha.label}
              </span>
              <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                <span
                  className="block h-full rounded-full bg-violet-400"
                  style={{ width: `${(linha.valor / maior) * 100}%` }}
                />
              </span>
              <span className="w-28 shrink-0 text-right text-sm font-semibold tabular-nums text-slate-800">
                {formatToReal(linha.valor)}
              </span>
            </div>
          ))}
        </div>
      )}

      {atualizacao && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-5 py-2 text-[11px] text-slate-500">
          Atualizado em {atualizacao}
        </div>
      )}
    </div>
  );
}

function TypeSection({ icon, titulo, type, instances }) {
  const blocks = useMemo(() => {
    const perInstance = instances
      .map((inst) => ({ inst, ...getTypeForInstance(inst, type) }))
      .filter((b) => b.present);
    // Junta as sub-fontes de uma MESMA fonte (title) num bloco só, para não
    // repetir "Fonte N" (ex.: spcSerasaPF + spcMixTopPF = Fonte 3; ou vários
    // birôs Boa Vista = Fonte 1). O bloco só fica "indisponível" se TODAS as
    // sub-fontes daquele título estiverem indisponíveis.
    const byTitle = new Map();
    for (const b of perInstance) {
      const cur = byTitle.get(b.inst.title);
      if (!cur) {
        byTitle.set(b.inst.title, {
          inst: b.inst,
          items: [...b.items],
          quantidadeTotal: b.quantidadeTotal || 0,
          valorTotal: b.valorTotal || 0,
          chartSeries: b.chartSeries || [],
          indisponivel: !!b.inst.indisponivel,
        });
      } else {
        cur.items.push(...b.items);
        cur.quantidadeTotal += b.quantidadeTotal || 0;
        cur.valorTotal += b.valorTotal || 0;
        if (b.chartSeries?.length && !cur.chartSeries?.length) {
          cur.chartSeries = b.chartSeries;
        }
        cur.indisponivel = cur.indisponivel && !!b.inst.indisponivel;
      }
    }
    return [...byTitle.values()];
  }, [instances, type]);

  // Nenhuma fonte reporta esse tipo -> não mostra a seção.
  if (!blocks.length) return null;

  // Sócios e administradores saem numa tabela só, com as fontes juntas.
  if (MERGED_TYPES.has(type)) {
    return (
      <MergedPessoasSection
        icon={icon}
        titulo={titulo}
        type={type}
        blocks={blocks}
      />
    );
  }

  return (
    <div className="mt-2">
      <TitleStripe className="mb-2">
        <div className="flex items-center gap-2">
          {icon}
          <span>{titulo}</span>
        </div>
      </TitleStripe>
      {blocks.map((b) => (
        <FonteBlock key={b.inst.key} title={b.inst.title}>
          {b.indisponivel ? (
            <FonteIndisponivel />
          ) : b.items.length > 0 || b.chartSeries?.length > 0 ? (
            <SafeRender label={`${titulo} · ${b.inst.title}`}>
              {renderDetail(type, b, b.inst)}
            </SafeRender>
          ) : (
            <NoOcurrence />
          )}
        </FonteBlock>
      ))}
    </div>
  );
}

/* Faixa de risco a partir do score (0–1000). */
function faixaRisco(score) {
  if (score >= 800) return { label: "Risco muito baixo", short: "Excelente" };
  if (score >= 600) return { label: "Risco baixo", short: "Bom" };
  if (score >= 400) return { label: "Risco médio", short: "Regular" };
  if (score >= 200) return { label: "Risco alto", short: "Baixo" };
  return { label: "Risco muito alto", short: "Crítico" };
}

/* Medidor radial (donut) do score — arco preenchido proporcional à nota,
   colorido pela faixa de risco, com o número e a letra no centro. */
function ScoreGauge({ score, color, letra, unavailable = false }) {
  const size = 148;
  const stroke = 13;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = unavailable
    ? 0
    : Math.max(0, Math.min(100, (score / 1000) * 100));
  const dash = (pct / 100) * c;

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#eef2f6"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dasharray .6s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center px-5">
        {unavailable ? (
          <span className="text-center text-xs font-bold leading-4 text-slate-500">
            Não foi possível calcular
          </span>
        ) : (
          <>
            <span
              className="text-4xl font-bold leading-none tabular-nums"
              style={{ color }}
            >
              {score}
            </span>
            <span className="mt-0.5 text-[10px] font-medium text-slate-400">
              de 1000
            </span>
            <span
              className="mt-1 inline-flex min-w-[26px] items-center justify-center rounded-md px-1.5 py-0.5 text-[11px] font-bold text-white shadow-sm"
              style={{ backgroundColor: color }}
            >
              {letra}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function ScoreCard({ s }) {
  const unavailable =
    s.unavailable || s.score == null || s.score === 0 || s.score === 2;
  const color = unavailable ? "#94a3b8" : getColorScore(s.score) || "#027B01";
  const letra = unavailable ? null : getLetterScore(s.score);
  const faixa = unavailable ? null : faixaRisco(s.score);

  return (
    <div
      className="group relative flex flex-col items-center overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 text-center shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
      style={{
        background: `linear-gradient(180deg, ${color}0f 0%, #ffffff 42%)`,
      }}
    >
      {/* faixa de cor no topo — com raio próprio para não vazar do card na
          impressão/PDF, onde o overflow-hidden do pai não recorta filhos */}
      <div
        className="absolute inset-x-0 top-0 h-1.5 rounded-t-2xl"
        style={{ backgroundColor: color }}
      />

      <span className="mb-4 mt-1 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">
        {s.title}
      </span>

      <ScoreGauge
        score={s.score}
        color={color}
        letra={letra}
        unavailable={unavailable}
      />

      <span
        className="mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
        style={{ backgroundColor: `${color}1a`, color }}
      >
        <span
          className="h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: color }}
        />
        {unavailable ? "Score indisponível" : faixa.label}
      </span>

      {!unavailable && s.prob != null && (
        <div className="mt-4 flex w-full items-center justify-between border-t border-slate-100 pt-3">
          <span className="text-[11px] uppercase tracking-wide text-slate-400">
            Prob. inadimplência
          </span>
          <span className="text-sm font-semibold text-slate-700 tabular-nums">
            {s.prob.toFixed(1)}%
          </span>
        </div>
      )}
    </div>
  );
}

function ScoreComparativo({ scores }) {
  if (!scores.length) return null;
  return (
    <div className="mt-2">
      <TitleStripe className="mb-4">
        <div className="flex items-center gap-2">
          <TfiBarChart className="text-blue-700" />
          <span>Score — comparativo entre fontes</span>
        </div>
      </TitleStripe>
      <div className="grid grid-cols-1 gap-4 grid-cols-3">
        {scores.map((s) => (
          <ScoreCard key={s.title} s={s} />
        ))}
      </div>
      <p className="mt-3 text-xs italic text-slate-500">
        A pontuação pode divergir entre fontes por consolidarem bases e
        critérios distintos. Para o relatório completo de cada fonte, use as
        abas acima.
      </p>
    </div>
  );
}

/* O rating pode vir no topo ou encapsulado em um item de `additional`.
   Normaliza os formatos conhecidos para mantê-lo junto ao comparativo. */
function getScoreRating(mergedBag) {
  const unwrap = (node) => {
    if (!node || typeof node !== "object") return null;

    const candidates = [
      node?.scoreRating,
      node?.consultaCredito?.scoreRating,
      node?.data?.scoreRating,
      node?.data?.consultaCredito?.scoreRating,
      node?.data,
      node?.consultaCredito,
    ];
    const nested = candidates.find(
      (candidate) =>
        candidate &&
        typeof candidate === "object" &&
        (candidate?.score != null || candidate?.statusRetorno != null),
    );
    if (nested) return nested;

    if (node?.score != null || node?.statusRetorno != null) return node;
    return null;
  };

  const direct = unwrap(mergedBag?.scoreRating);
  if (direct) return direct;

  const additional = mergedBag?.additional;
  const additionalItems = Array.isArray(additional)
    ? additional
    : additional && typeof additional === "object"
      ? Object.values(additional)
      : [];

  for (const item of additionalItems) {
    const rating = unwrap(item);
    if (
      item?.consultaCredito?.scoreRating ||
      item?.data?.scoreRating ||
      item?.data?.consultaCredito?.scoreRating
    ) {
      return rating;
    }
  }

  return null;
}

const isBlank = (v) => v == null || String(v).trim() === "";

/* Só esconde o card se houver um módulo cadastral dedicado REALMENTE ativo
   (senão o cadastro sumiria: escondido no componente e sem módulo pra cobrir). */
function hasActiveDedicatedCadastral(mergedBag) {
  const totalCadastros = countDadosCadastrais(mergedBag);
  return DEDICATED_CADASTRAL_KEYS.some((k) => {
    const n = mergedBag?.[k];
    if (!n || typeof n !== "object") return false;
    // Módulo premium com 2+ cadastros no relatório não renderiza nada — ignora.
    if (SELF_SUPPRESSING_CADASTRAL_KEYS.has(k) && totalCadastros >= 2) {
      return false;
    }
    const dto = getDto(n);
    return (
      n.statusRetorno == 1 ||
      dto?.statusRetorno == 1 ||
      !!dto?.dadosCadastrais ||
      !!dto?.razaoSocial ||
      !!dto?.nome
    );
  });
}

/* Monta o nome, o rótulo e a lista de campos exibíveis a partir do bloco
   `dadosCadastrais` de UMA fonte. */
/* Capital vem como "99314861.00" (string, ponto decimal) ou já como número.
   Zero e vazio somem do card: é o valor de "não informado" nesses campos. */
function dinheiroCadastral(valor) {
  if (isBlank(valor)) return null;
  const numero = parseValor(valor);
  return numero > 0 ? formatToReal(numero) : null;
}

function buildCadastro(c) {
  if (!c || typeof c !== "object") return null;

  const get = (...keys) => {
    for (const k of keys) if (!isBlank(c[k])) return c[k];
    return null;
  };

  // Alguns campos vêm com placeholders de "sem informação" que não agregam —
  // tratados como vazios para não poluir o card.
  const NAO_INFORMADO = new Set([
    "não informado",
    "nao informado",
    "indisponível",
    "indisponivel",
    "não consta",
    "nao consta",
    "-",
  ]);
  const getM = (...keys) => {
    for (const k of keys) {
      const v = c[k];
      if (!isBlank(v) && !NAO_INFORMADO.has(String(v).trim().toLowerCase())) {
        return v;
      }
    }
    return null;
  };

  const nome = c.nome || c.razaoSocial;
  const isPJ = !!(c.razaoSocial || c.cnpj);
  const doc = c.cpf || c.cnpj;
  const enderecoLinha = [c.endereco || c.logradouro, c.numero, c.complemento]
    .filter((x) => !isBlank(x))
    .join(", ");

  const campos = [
    { label: "Documento", value: doc ? formatDocument(doc) : null },
    { label: "Nome fantasia", value: get("nomeFantasia") },
    {
      label: isPJ ? "Fundação" : "Nascimento",
      value: get("dataFundacao", "dataInicioAtividade", "dataNascimento"),
    },
    { label: "Idade", value: get("idade") },
    { label: "Sexo", value: getM("sexo") },
    { label: "Estado civil", value: getM("estadoCivil") },
    { label: "Grau de instrução", value: getM("grauInstrucao") },
    { label: "Situação", value: getM("situacao", "situacaoCadastral") },
    { label: "Origem do CPF", value: getM("regiaoCPF") },
    { label: "Nº de dependentes", value: getM("numeroDependentes") },
    /* Renda/faturamento saíram daqui para a seção "Renda presumida": são
       valores que se comparam entre fontes, e comparação pede seção própria —
       no meio de nome, mãe e endereço eles passavam batidos. */
    { label: "Profissão (CBO)", value: getM("cbo") },
    { label: "Nome da mãe", value: get("nomeMae") },
    { label: "Nome do pai", value: get("nomePai") },
    {
      label: "Óbito",
      value: typeof c.obito === "boolean" ? (c.obito ? "Sim" : "Não") : null,
    },
    { label: "Natureza jurídica", value: get("naturezaJuridica") },
    /* Vêm como string crua do birô ("99314861.00"): sem formatar viravam um
       número solto no card, e capital é dinheiro. Capital inicial zerado é o
       valor de quem nunca informou, então só aparece quando existe de fato. */
    { label: "Capital social", value: dinheiroCadastral(c.capitalSocial) },
    { label: "Capital inicial", value: dinheiroCadastral(c.capitalInicial) },
    {
      label: "Atividade / CNAE",
      value: get(
        "cnae",
        "atividadePrincipal",
        "cnaeDescricao",
        "ramoAtividade",
      ),
    },
    { label: "Porte", value: getM("porte") },
    { label: "Situação especial", value: getM("situacaoEspecial") },
    { label: "Faixa de funcionários", value: getM("faixaFuncionarios") },
    { label: "Endereço", value: isBlank(enderecoLinha) ? null : enderecoLinha },
    { label: "Bairro", value: get("bairro") },
    {
      label: "Cidade / UF",
      value: [c.cidade, c.uf].filter((x) => !isBlank(x)).join(" / ") || null,
    },
    { label: "CEP", value: get("cep") },
    { label: "Telefone", value: get("telefone", "celular") },
    { label: "E-mail", value: get("email") },
  ].filter((f) => !isBlank(f.value));

  if (!campos.length && isBlank(nome)) return null;
  return { nome, campos };
}

/* defineLimite/defineLimitePositivo trazem o cadastro num formato próprio
   (identificacao_completo + localizacao_completo.matriz) em vez de
   dadosCadastrais. Normaliza para o mesmo shape que buildCadastro consome. */
function normalizeAltCadastral(node) {
  // O node pode chegar cru ou embrulhado (additional: { data: {...} }).
  const root = node?.identificacao_completo
    ? node
    : node?.data?.identificacao_completo
      ? node.data
      : node;
  const id = root?.identificacao_completo;
  if (!id || typeof id !== "object") return null;
  const matriz = root?.localizacao_completo?.matriz || {};
  /* Ramo primário com código e descrição, como os demais birôs mandam
     ("4755-5/03 | COM VAREJ ARTIGOS DE CAMA MESA BANHO"). */
  const ramoPrimario = [
    id.ramo_atividade_primario?.cnae,
    id.ramo_atividade_primario?.atividade,
  ]
    .filter(Boolean)
    .join(" | ");

  return {
    razaoSocial: id.razao_social,
    nomeFantasia: id.nome_fantasia,
    cnpj: id.cnpj,
    situacao: id.situacao_cnpj,
    dataFundacao: id.data_fundacao,
    naturezaJuridica: id.natureza_juridica,
    // Aqui o capital vigente chama `capital_atual`; nos outros, `capitalSocial`.
    capitalSocial: id.capital_atual ?? id.capital_social,
    capitalInicial: id.capital_inicial,
    cnae: ramoPrimario || id.ramo_atividade_primario?.atividade,
    endereco: matriz.endereco_matriz,
    bairro: matriz.bairro_matriz,
    cep: matriz.cep_matriz,
    cidade: matriz.cidade_matriz,
    uf: matriz.uf_matriz,
  };
}

/* Campos longos ocupam a linha inteira: espremidos em 1/3 de largura eles
   quebravam em três linhas enquanto "Sexo" sobrava espaço ao lado. */
const CAMPOS_LARGOS = new Set([
  "Endereço",
  "Atividade / CNAE",
  "Natureza jurídica",
  "Nome da mãe",
  "Nome do pai",
  "Nome fantasia",
  "Renda presumida",
  "Faturamento médio (CNAE)",
  "Profissão (CBO)",
  "E-mail",
]);

// Vão para o cabeçalho em vez da grade: identificam a pessoa/empresa.
const CAMPO_DOCUMENTO = "Documento";
const CAMPO_SITUACAO = "Situação";

/* Card de cadastro de UMA fonte. Documento e situação sobem para o cabeçalho,
   junto do nome, porque são o que identifica o consultado; o resto vai para uma
   grade responsiva de linhas rotuladas.

   Antes eram caixas cinza numa grade fixa de 3 colunas: no mobile virava três
   colunas minúsculas e, no desktop, "Endereço" e "Atividade / CNAE" quebravam
   em várias linhas dentro de 1/3 da largura enquanto "Sexo" sobrava espaço. */
function CadastroCampos({ nome, campos, fonte }) {
  const documento = campos.find((c) => c.label === CAMPO_DOCUMENTO);
  const situacao = campos.find((c) => c.label === CAMPO_SITUACAO);
  const demais = campos.filter(
    (c) => c.label !== CAMPO_DOCUMENTO && c.label !== CAMPO_SITUACAO,
  );

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white px-4 py-3">
        <div className="min-w-0">
          {nome && (
            <p className="font-semibold leading-tight text-slate-900 text-lg">
              {nome}
            </p>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            {documento && (
              <span className="text-sm tabular-nums text-slate-600">
                {documento.value}
              </span>
            )}
            {situacao && (
              <span className="rounded-full bg-slate-200/70 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                {situacao.value}
              </span>
            )}
          </div>
        </div>
        {fonte && (
          <span className="shrink-0 rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-blue-700">
            {fonte}
          </span>
        )}
      </div>

      <div className="grid grid-cols-3">
        {demais.map((campo) => (
          <div
            key={campo.label}
            className={[
              "border-b border-slate-100 px-4 py-2.5",
              CAMPOS_LARGOS.has(campo.label) ? "col-span-3" : "",
            ].join(" ")}
          >
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              {campo.label}
            </p>
            <p className="mt-0.5 text-sm font-semibold text-slate-900">
              {campo.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Mescla vários blocos `dadosCadastrais` num só: para cada campo, o primeiro
   valor não-vazio encontrado. Usado para consolidar as sub-fontes de UMA mesma
   fonte (ex.: Boa Vista pode trazer defineRisco COM cadastro e
   acertaEssencialPositivo SEM — o merge preenche o que faltar). */
function mergeCadastrais(list) {
  const merged = {};
  for (const c of list) {
    if (!c || typeof c !== "object") continue;
    for (const [k, v] of Object.entries(c)) {
      if (isBlank(merged[k]) && !isBlank(v)) merged[k] = v;
    }
  }
  return merged;
}

function CadastroConsolidado({ instances, mergedBag }) {
  if (hasActiveDedicatedCadastral(mergedBag)) return null;

  // Um cadastro por FONTE (title): mescla o dadosCadastrais de TODAS as
  // sub-fontes daquela fonte (ex.: spcSerasaPF + spcMixTopPF = Fonte 3; ou
  // defineRisco + acertaEssencialPositivo = Fonte 1), sem repetir "Fonte N" e
  // preenchendo o que uma sub-fonte não trouxer com o que a outra trouxe.
  const byTitle = new Map();
  for (const inst of instances) {
    if (!byTitle.has(inst.title)) {
      byTitle.set(inst.title, { key: inst.key, title: inst.title, list: [] });
    }
    byTitle.get(inst.title).list.push(inst.dto?.dadosCadastrais);
  }

  const blocos = [];
  for (const { key, title, list } of byTitle.values()) {
    const cadastro = buildCadastro(mergeCadastrais(list));
    if (cadastro) blocos.push({ key, title, cadastro });
  }

  if (!blocos.length) return null;

  return (
    <div className="mt-2">
      <TitleStripe className="mb-4">
        <div className="flex items-center gap-2">
          <FiUserCheck className="text-blue-700" />
          <span>Cadastro</span>
        </div>
      </TitleStripe>
      {/* Um card por fonte: o cabeçalho do card já traz a etiqueta "Fonte N",
          então dispensa o FonteBlock por fora. */}
      <div className="space-y-4">
        {blocos.map((b) => (
          <CadastroCampos
            key={b.key}
            nome={b.cadastro.nome}
            campos={b.cadastro.campos}
            fonte={b.title}
          />
        ))}
      </div>
    </div>
  );
}

/* Extras por fonte: para as fontes "ricas" (Boa Vista), renderiza o componente
   INTEIRO com hideAgrupados, mostrando só os blocos bespoke (comportamental,
   estimativas, faturamento, falências...) que não entram nos grupos por tipo. */
function ExtrasPorFonte({ instances, renderComponentByKey, doc }) {
  if (typeof renderComponentByKey !== "function") return null;
  const richItems = instances.filter((i) => i.hasExtras);
  if (!richItems.length) return null;

  return (
    <div className="mt-2">
      <TitleStripe className="mb-2">
        <div className="flex items-center gap-2">
          <TbListDetails className="text-blue-700" />
          <span>Informações adicionais por fonte</span>
        </div>
      </TitleStripe>
      {richItems.map((inst) => (
        <FonteBlock key={inst.key} title={inst.title}>
          <SafeRender label={`Extras · ${inst.title}`}>
            {renderComponentByKey(inst.key, inst.renderItem, doc, {
              hideAgrupados: true,
            })}
          </SafeRender>
        </FonteBlock>
      ))}
    </div>
  );
}

export default function ConsolidatedByType({
  instances = [],
  mergedBag,
  renderComponentByKey,
  document: doc,
}) {
  // Instâncias exibidas: birôs padrão + a instância normalizada do Define
  // (Fonte 1) + protestos nacionais (CENPROT) + cadastro interno ("Fonte
  // interna"), ordenadas por fonte (1 antes de 2, 3…; rótulos sem número por
  // último, na ordem em que foram anexados).
  const allInstances = useMemo(() => {
    const def = buildDefineInstance(mergedBag);
    const cenprot = buildProtestoNacionalInstances(mergedBag);
    const interna = buildCadastroInternoInstances(mergedBag);
    const ccf = buildChequesSemFundoInstances(mergedBag);
    return sortByFonte([
      ...instances,
      ...(def ? [def] : []),
      ...cenprot,
      ...ccf,
      ...interna,
    ]);
  }, [instances, mergedBag]);

  // Um score por FONTE (dedup por title): se a Fonte 3 tem spcSerasaPF +
  // spcMixTopPF, mostra só um card (o primeiro com score válido). CENPROT e a
  // Fonte interna ficam de fora: nunca trazem score.
  const scores = useMemo(() => {
    const byTitle = new Map();
    const scoraveis = allInstances.filter(
      (i) => !i.isProtestoNacional && !i.isCadastroInterno,
    );
    for (const s of extractScores(scoraveis)) {
      if (!byTitle.has(s.title)) byTitle.set(s.title, s);
    }
    return [...byTitle.values()];
  }, [allInstances]);

  const scoreRating = useMemo(() => getScoreRating(mergedBag), [mergedBag]);
  const hasScoreRating =
    scoreRating?.score != null &&
    (scoreRating?.statusRetorno == null ||
      Number(scoreRating.statusRetorno) === 1);

  if (!allInstances.length) return null;

  return (
    <div>
      <SafeRender label="Cadastro">
        <CadastroConsolidado instances={allInstances} mergedBag={mergedBag} />
      </SafeRender>
      <SafeRender label="Score">
        <ScoreComparativo scores={scores} />
      </SafeRender>
      {hasScoreRating && (
        <SafeRender label="Score Rating">
          <ScoreRating
            score={{ ...scoreRating, statusRetorno: 1 }}
            block={false}
          />
        </SafeRender>
      )}

      <TypeSection
        type="renda"
        titulo="Renda presumida"
        icon={<HiOutlineCurrencyDollar className="text-blue-700" />}
        instances={allInstances}
      />
      <TypeSection
        type="participacao"
        titulo="Participação em empresas"
        icon={<LuBuilding2 className="text-blue-700" />}
        instances={allInstances}
      />
      {/* Logo abaixo da participação da própria empresa: é a mesma pergunta
          ("onde mais isso aparece"), só que olhando para as pessoas. */}
      <ParticipacoesSociosSection
        titulo="Participações dos sócios e administradores"
        icon={<LuBuilding2 className="text-blue-700" />}
        instances={allInstances}
      />
      <TypeSection
        type="socios"
        titulo="Quadro societário"
        icon={<FiUsers className="text-blue-700" />}
        instances={allInstances}
      />
      <TypeSection
        type="administradores"
        titulo="Administradores"
        icon={<FiUsers className="text-blue-700" />}
        instances={allInstances}
      />
      <TypeSection
        type="enderecos"
        titulo="Endereços"
        icon={<HiOutlineLocationMarker className="text-blue-700" />}
        instances={allInstances}
      />
      <TypeSection
        type="telefones"
        titulo="Telefones"
        icon={<FiPhone className="text-blue-700" />}
        instances={allInstances}
      />
      <TypeSection
        type="emails"
        titulo="E-mails"
        icon={<HiOutlineMail className="text-blue-700" />}
        instances={allInstances}
      />

      <TypeSection
        type="pendencias"
        titulo="Pendências financeiras"
        icon={<HiOutlineCurrencyDollar className="text-blue-700" />}
        instances={allInstances}
      />
      <TypeSection
        type="protestos"
        titulo="Protestos"
        icon={<GrDocumentExcel className="text-blue-700" />}
        instances={allInstances}
      />
      <TypeSection
        type="cheques"
        titulo="Cheques sem fundo"
        icon={<CiMoneyCheck1 className="text-blue-700" />}
        instances={allInstances}
      />
      <TypeSection
        type="chequesSustados"
        titulo="Cheques sustados"
        icon={<CiMoneyCheck1 className="text-blue-700" />}
        instances={allInstances}
      />
      <TypeSection
        type="acoes"
        titulo="Ações cíveis"
        icon={<MdOutlineBalance className="text-blue-700" />}
        instances={allInstances}
      />
      {/* Falência/recuperação judicial é restritivo: fica junto das ações
          cíveis, e não perdido no fim, dentro dos extras de cada fonte. */}
      <TypeSection
        type="falencias"
        titulo="Falências/Recuperações Judiciais"
        icon={<TfiBarChart className="text-blue-700" />}
        instances={allInstances}
      />
      <TypeSection
        type="historico"
        titulo="Histórico de consultas"
        icon={<GrDocumentTime className="text-blue-700" />}
        instances={allInstances}
      />

      <ExtrasPorFonte
        instances={allInstances}
        renderComponentByKey={renderComponentByKey}
        doc={doc}
      />
    </div>
  );
}
